/**
 * Full-page payment receipt: summary and breakdown (primary invoice + adjustments by source invoice).
 * Reached via Finances or Practice Finances payment row click.
 */

import {
  Anchor,
  Badge,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconDownload } from '@tabler/icons-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  formatCurrency,
  MOCK_PAYMENTS,
  type Payment,
  type SessionLineItem,
} from './FinancesPage';

function AdjustmentLinesTable({ rows }: { rows: SessionLineItem[] }) {
  return (
    <Table highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Date</Table.Th>
          <Table.Th>Client</Table.Th>
          <Table.Th>Provider</Table.Th>
          <Table.Th>CPT Code</Table.Th>
          <Table.Th>Reason</Table.Th>
          <Table.Th>Amount</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((s) => (
          <Table.Tr key={s.id}>
            <Table.Td><Text size="sm">{s.date}</Text></Table.Td>
            <Table.Td><Text size="sm">{s.client}</Text></Table.Td>
            <Table.Td><Text size="sm">{s.provider}</Text></Table.Td>
            <Table.Td><Text size="sm" ff="monospace">{s.cptCode}</Text></Table.Td>
            <Table.Td><Text size="sm">{s.reason ?? '—'}</Text></Table.Td>
            <Table.Td><Text size="sm">{formatCurrency(s.amount)}</Text></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export function PaymentDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/payment\/[^/]+$/, '');
  const backUrl = `${basePath}?tab=payments`;
  const payment: Payment | undefined = id ? MOCK_PAYMENTS.find((p) => p.id === id) : undefined;

  if (!payment) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Button variant="subtle" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate(backUrl)}>
            Back to payments
          </Button>
          <Text c="dimmed">Payment not found.</Text>
        </Stack>
      </Container>
    );
  }

  const statusColor =
    payment.status === 'Succeeded' ? 'green'
    : payment.status === 'Processing' ? 'blue'
    : payment.status === 'Scheduled' ? 'gray'
    : 'red';

  const amountColumnWidth = 120;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate(backUrl)}
          >
            Back to payments
          </Button>
          <Button leftSection={<IconDownload size={18} />}>
            Download as PDF
          </Button>
        </Group>

        <Paper withBorder p="lg" radius="md">
          <Title order={2} mb="xs">Payment Receipt</Title>
          <Text size="sm" c="gray.6" fw={600} mb="md">Payment date: {payment.date}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="lg">
            <div>
              <Text size="xs" c="gray.6" fw={600}>Net Amount</Text>
              <Text fw={600}>{formatCurrency(payment.amount)}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Payment Method</Text>
              <Text size="sm">{payment.paymentMethod}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Status</Text>
              <Badge size="sm" variant="light" color={statusColor}>
                {payment.status}
              </Badge>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Stripe Payment Intent ID</Text>
              <Text size="sm" ff="monospace">{payment.stripePaymentIntentId}</Text>
            </div>
          </SimpleGrid>

          <Text size="sm" fw={600} mb="sm">Receipt breakdown</Text>
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Group gap="xs">
                <Text size="sm" c="gray.6">from</Text>
                <Anchor size="sm" component={Link} to={`${backUrl}/invoice/${payment.primaryInvoiceId}`}>
                  Invoice: {payment.primaryInvoiceLabel}
                </Anchor>
              </Group>
              <Text size="sm" style={{ minWidth: amountColumnWidth, textAlign: 'right' }}>
                + {formatCurrency(payment.primaryAmount)}
              </Text>
            </Group>

            {payment.invoiceAdjustments.length > 0 && (
              <>
                {payment.invoiceAdjustments.map((group) => {
                  const subtotal = group.adjustments.reduce((sum, a) => sum + a.amount, 0);
                  return (
                    <Stack key={group.invoiceId} gap="xs" pl="md" style={{ borderLeft: '2px solid var(--mantine-color-gray-3)' }}>
                      <Group justify="space-between" align="center" wrap="nowrap">
                        <Text size="sm" fw={500}>
                          Invoice: {group.invoiceLabel} — {group.adjustments.length} adjustment{group.adjustments.length !== 1 ? 's' : ''}
                        </Text>
                        <Text size="sm" style={{ minWidth: amountColumnWidth, textAlign: 'right' }}>
                          {formatCurrency(subtotal)}
                        </Text>
                      </Group>
                      <AdjustmentLinesTable rows={group.adjustments} />
                    </Stack>
                  );
                })}
              </>
            )}

            <Divider />
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text size="sm" fw={600}>Net payment</Text>
              <Text size="sm" fw={600} style={{ minWidth: amountColumnWidth, textAlign: 'right' }}>
                {formatCurrency(payment.amount)}
              </Text>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
